import { useEffect, useState } from "react";
import {
  Timestamp,
  doc,
  getFirestore,
  onSnapshot,
} from "firebase/firestore";

export type PaciHealthStatus = "healthy" | "degraded" | "down" | "unknown";

export type PaciHealthIncident = {
  id?: string;
  severity?: "degraded" | "down";
  policyDisplayName?: string;
  conditionDisplayName?: string;
  summary?: string;
  url?: string;
  openedAt?: string;
  observedAt?: string;
};

export type PaciHealthNotification = {
  state?: "open" | "closed";
  severity?: "degraded" | "down";
  policyDisplayName?: string;
  conditionDisplayName?: string;
  summary?: string;
  url?: string;
  observedAt?: string;
};

export type PaciHealthState = {
  status: PaciHealthStatus;
  message: string;
  activeIncidentCount: number;
  activeIncidents: PaciHealthIncident[];
  updatedAt: Timestamp | null;
  lastEventAt: Timestamp | null;
  lastNotification?: PaciHealthNotification;
  source?: string;
  loading: boolean;
  error?: Error;
};

const DEFAULT_STATE: PaciHealthState = {
  status: "unknown",
  message: "PACI health has not been initialized.",
  activeIncidentCount: 0,
  activeIncidents: [],
  updatedAt: null,
  lastEventAt: null,
  loading: true,
};

const isRecord = (value: unknown): value is Record<string, unknown> => (
  value !== null && typeof value === "object" && !Array.isArray(value)
);

const stringValue = (value: unknown) => (
  typeof value === "string" && value.trim() ? value.trim() : undefined
);

const statusValue = (value: unknown): PaciHealthStatus => {
  if (value === "healthy" || value === "degraded" || value === "down") {
    return value;
  }

  return "unknown";
};

const severityValue = (value: unknown) => {
  if (value === "degraded" || value === "down") {
    return value;
  }

  return undefined;
};

const notificationStateValue = (value: unknown) => {
  if (value === "open" || value === "closed") {
    return value;
  }

  return undefined;
};

const timestampValue = (value: unknown) => (
  value instanceof Timestamp ? value : null
);

const incidentFromRecord = (record: Record<string, unknown>): PaciHealthIncident => ({
  id: stringValue(record.id),
  severity: severityValue(record.severity),
  policyDisplayName: stringValue(record.policyDisplayName),
  conditionDisplayName: stringValue(record.conditionDisplayName),
  summary: stringValue(record.summary),
  url: stringValue(record.url),
  openedAt: stringValue(record.openedAt),
  observedAt: stringValue(record.observedAt),
});

const notificationFromRecord = (record: Record<string, unknown>): PaciHealthNotification => ({
  state: notificationStateValue(record.state),
  severity: severityValue(record.severity),
  policyDisplayName: stringValue(record.policyDisplayName),
  conditionDisplayName: stringValue(record.conditionDisplayName),
  summary: stringValue(record.summary),
  url: stringValue(record.url),
  observedAt: stringValue(record.observedAt),
});

const compareIncidents = (a: PaciHealthIncident, b: PaciHealthIncident) => {
  if (a.severity !== b.severity) {
    return a.severity === "down" ? -1 : 1;
  }

  return String(b.observedAt ?? "").localeCompare(String(a.observedAt ?? ""));
};

export function usePaciHealth(): PaciHealthState {
  const [state, setState] = useState<PaciHealthState>(DEFAULT_STATE);

  useEffect(() => {
    const db = getFirestore();
    const healthRef = doc(db, "temp", "paci_health");

    return onSnapshot(
      healthRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          setState({
            ...DEFAULT_STATE,
            loading: false,
          });
          return;
        }

        const data = snapshot.data();
        const rawIncidents = isRecord(data.activeIncidents) ? data.activeIncidents : {};
        const activeIncidents = Object.values(rawIncidents)
          .filter(isRecord)
          .map(incidentFromRecord)
          .sort(compareIncidents);
        const rawNotification = data.lastNotification;

        setState({
          status: statusValue(data.status),
          message: stringValue(data.message) ?? "PACI health state is available.",
          activeIncidentCount: typeof data.activeIncidentCount === "number"
            ? data.activeIncidentCount
            : activeIncidents.length,
          activeIncidents,
          updatedAt: timestampValue(data.updatedAt),
          lastEventAt: timestampValue(data.lastEventAt),
          lastNotification: isRecord(rawNotification)
            ? notificationFromRecord(rawNotification)
            : undefined,
          source: stringValue(data.source),
          loading: false,
        });
      },
      (error) => {
        setState({
          ...DEFAULT_STATE,
          status: "unknown",
          message: "Unable to read PACI health.",
          loading: false,
          error,
        });
      },
    );
  }, []);

  return state;
}
